import { useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { type EntityLookupResponse } from '@air/api';
import { type BaseCustomField } from '@air/api/types';

import { useCreateParamsFromSavedFilters } from '~/components/SavedFilter/shared/hooks/useCreateParamsFromSavedFilters';
import { useGetFilterParamsWithTypeFilter } from '~/components/SavedFilter/shared/hooks/useGetFilterParamsWithTypesFilter';
import { selectedFiltersValuesSelector } from '~/store/filters/selectors';
import { type FilterLookupsOptions } from '~/swr-hooks/filters/types';
import { savedFilterCriteriaNeedsPlainTextCatalog } from '~/utils/filters/plainTextCustomFieldFilter';
import { entityLookupWithCatalogCustomFields } from '~/utils/filters/urlParamsToFiltersUtils';
import { useAirSelector } from '~/utils/ReduxUtils';

export const useDidFilterChange = ({
  options,
  entities,
  catalogCustomFields,
}: {
  options?: FilterLookupsOptions;
  entities?: EntityLookupResponse;
  catalogCustomFields: BaseCustomField[] | undefined;
}) => {
  const { createParamsFromSavedFilters } = useCreateParamsFromSavedFilters();
  const filterParams = useGetFilterParamsWithTypeFilter();
  const selectedFiltersInStore = useAirSelector(selectedFiltersValuesSelector);

  const isFilterChanged = useMemo(() => {
    if (savedFilterCriteriaNeedsPlainTextCatalog(filterParams) && catalogCustomFields === undefined) {
      return false;
    }

    if (filterParams && options && entities) {
      const filterParamsConverted = createParamsFromSavedFilters(
        filterParams,
        options,
        entityLookupWithCatalogCustomFields({
          entities,
          catalogFields: catalogCustomFields ?? [],
        }),
      );

      return !isEqual(filterParamsConverted, selectedFiltersInStore);
    }

    return false;
  }, [catalogCustomFields, createParamsFromSavedFilters, entities, filterParams, options, selectedFiltersInStore]);

  return { isFilterChanged };
};
